import { useState } from 'react';
import Select from 'react-select';
import { logEntryApi } from '../api/logbookApi';
import './NewEntryModal.css';
import { useOffline } from '../context/OfflineContext';

const CATEGORY_CONFIG = {
  other:    { label: 'Egyéb / parkoló', icon: 'ti-parking' },
  pickup:   { label: 'Felrakó',         icon: 'ti-package' },
  delivery: { label: 'Lerakó',          icon: 'ti-package-export' },
  customs:  { label: 'Vámkezelés',      icon: 'ti-building-bank' },
  refuel:   { label: 'Tankolás',        icon: 'ti-gas-station' },
};

const COUNTRIES = [
  { value: 'AD', label: 'AD – Andorra' },
  { value: 'AL', label: 'AL – Albánia' },
  { value: 'AT', label: 'AT – Ausztria' },
  { value: 'BA', label: 'BA – Bosznia-Hercegovina' },
  { value: 'BE', label: 'BE – Belgium' },
  { value: 'BG', label: 'BG – Bulgária' },
  { value: 'BY', label: 'BY – Fehéroroszország' },
  { value: 'CH', label: 'CH – Svájc' },
  { value: 'CZ', label: 'CZ – Csehország' },
  { value: 'DE', label: 'DE – Németország' },
  { value: 'DK', label: 'DK – Dánia' },
  { value: 'EE', label: 'EE – Észtország' },
  { value: 'ES', label: 'ES – Spanyolország' },
  { value: 'FI', label: 'FI – Finnország' },
  { value: 'FR', label: 'FR – Franciaország' },
  { value: 'GB', label: 'GB – Nagy-Britannia' },
  { value: 'GE', label: 'GE – Grúzia' },
  { value: 'GR', label: 'GR – Görögország' },
  { value: 'HR', label: 'HR – Horvátország' },
  { value: 'HU', label: 'HU – Magyarország' },
  { value: 'IE', label: 'IE – Írország' },
  { value: 'IS', label: 'IS – Izland' },
  { value: 'IT', label: 'IT – Olaszország' },
  { value: 'LT', label: 'LT – Litvánia' },
  { value: 'LU', label: 'LU – Luxemburg' },
  { value: 'LV', label: 'LV – Lettország' },
  { value: 'MC', label: 'MC – Monaco' },
  { value: 'MT', label: 'MT – Málta' },
  { value: 'NL', label: 'NL – Hollandia' },
  { value: 'NO', label: 'NO – Norvégia' },
  { value: 'PL', label: 'PL – Lengyelország' },
  { value: 'PT', label: 'PT – Portugália' },
  { value: 'RO', label: 'RO – Románia' },
  { value: 'RS', label: 'RS – Szerbia' },
  { value: 'RU', label: 'RU – Oroszország' },
  { value: 'SE', label: 'SE – Svédország' },
  { value: 'SI', label: 'SI – Szlovénia' },
  { value: 'SK', label: 'SK – Szlovákia' },
  { value: 'TR', label: 'TR – Törökország' },
];

const selectStyles = {
  control: (base, state) => ({
    ...base,
    backgroundColor: '#ffffff',
    borderColor: state.isFocused ? '#2bb344' : '#e1e8e2',
    borderRadius: 10,
    padding: '4px 2px',
    boxShadow: 'none',
    fontSize: 16,
    '&:hover': { borderColor: '#2bb344' },
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: '#ffffff',
    border: '1px solid #e1e8e2',
    borderRadius: 10,
    overflow: 'hidden',
    zIndex: 20,
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#e8f9eb' : state.isFocused ? '#f7faf7' : 'transparent',
    color: state.isSelected ? '#1f8c34' : '#1c241e',
    cursor: 'pointer',
    fontSize: 15,
  }),
  singleValue: (base) => ({ ...base, color: '#1c241e' }),
  input: (base) => ({ ...base, color: '#1c241e' }),
  placeholder: (base) => ({ ...base, color: '#8a958c' }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base) => ({ ...base, color: '#8a958c' }),
};

function nowLocal() {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 16);
}

export default function EditEntryModal({ entry, onClose, onSaved }) {
  const cfg = CATEGORY_CONFIG[entry.category] || {};
  const isFreeText = ['other', 'refuel'].includes(entry.category);
  const { isOnline, updatePendingEntry } = useOffline();

  const [arrival, setArrival] = useState(entry.arrival ? entry.arrival.slice(0, 16) : nowLocal());
  const [departure, setDeparture] = useState(entry.departure ? entry.departure.slice(0, 16) : '');
  const [diesel, setDiesel] = useState(entry.diesel || '');
  const [adblue, setAdblue] = useState(entry.adblue || '');
  const [country, setCountry] = useState(
    entry.country ? COUNTRIES.find(c => c.value === entry.country) || null : null
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canEditArrival = !entry.arrival;
  const canEditDeparture = !entry.departure;
  const canEditDiesel = entry.category === 'refuel' && !entry.diesel;
  const canEditAdblue = entry.category === 'refuel' && !entry.adblue;
  const canEditCountry = isFreeText && !entry.country;

  const handleSave = async () => {
    setError('');
    const payload = {};
    if (canEditArrival && arrival) payload.arrival = arrival;
    if (canEditDeparture && departure) payload.departure = departure;
    if (canEditDiesel && diesel) payload.diesel = diesel;
    if (canEditAdblue && adblue) payload.adblue = adblue;
    if (canEditCountry && country) payload.country = country.value;

    if (Object.keys(payload).length === 0) {
      setError('Nincs módosítandó adat.');
      return;
    }

    setSaving(true);
    try {
      if (entry.isPending) {
        await updatePendingEntry(entry.local_id, payload);
      } else if (isOnline) {
        await logEntryApi.patch(entry.id, payload);
      } else {
        setError('Offline módban csak a függőben lévő bejegyzések szerkeszthetők.');
        return;
      }
      onSaved();
    } catch (err) {
      if (err.code === 'ECONNABORTED') {
        setError('A mentés túl sokáig tartott. Próbáld újra!');
      } else if (err.response?.status === 400) {
        setError('Hibás adatok. Ellenőrizd a mezőket!');
      } else {
        setError('Nem sikerült menteni a módosítást. Próbáld újra!');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            <i className={`ti ${cfg.icon}`}></i> {cfg.label} – módosítás
            {entry.isPending && (
              <span style={{ fontSize: 12, color: '#b8862e', marginLeft: 8 }}>
                (Függőben)
              </span>
            )}
          </h3>
          <button className="modal-close" onClick={onClose}>
            <i className="ti ti-x"></i>
          </button>
        </div>

        <div className="modal-body">
          <div className="edit-entry-city">
            <i className="ti ti-map-pin"></i>
            {entry.city}
            {entry.country && <span style={{ color: '#2473b8', marginLeft: 6 }}>({entry.country})</span>}
          </div>

          {canEditCountry && (
            <>
              <label>Ország</label>
              <Select
                options={COUNTRIES}
                value={country}
                onChange={setCountry}
                placeholder="Válassz országot..."
                noOptionsMessage={() => 'Nincs találat'}
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                isClearable
              />
            </>
          )}

          <label>
            Érkezés
            {!canEditArrival && <span className="field-locked"><i className="ti ti-lock"></i> Rögzítve</span>}
          </label>
          <input
            type="datetime-local"
            className={`entry-input ${!canEditArrival ? 'input-locked' : ''}`}
            value={arrival}
            onChange={e => setArrival(e.target.value)}
            disabled={!canEditArrival}
          />

          <label>
            Távozás
            {!canEditDeparture && <span className="field-locked"><i className="ti ti-lock"></i> Rögzítve</span>}
          </label>
          <input
            type="datetime-local"
            className={`entry-input ${!canEditDeparture ? 'input-locked' : ''}`}
            value={departure}
            onChange={e => setDeparture(e.target.value)}
            disabled={!canEditDeparture}
          />

          {entry.category === 'refuel' && (
            <div className="entry-time-row">
              <div className="entry-time-col">
                <label>
                  Dízel (liter)
                  {!canEditDiesel && <span className="field-locked"><i className="ti ti-lock"></i> Rögzítve</span>}
                </label>
                <input
                  type="number"
                  inputMode="decimal"
                  className={`entry-input ${!canEditDiesel ? 'input-locked' : ''}`}
                  placeholder="pl. 320"
                  value={diesel}
                  onChange={e => setDiesel(e.target.value)}
                  disabled={!canEditDiesel}
                />
              </div>
              <div className="entry-time-col">
                <label>
                  AdBlue (liter)
                  {!canEditAdblue && <span className="field-locked"><i className="ti ti-lock"></i> Rögzítve</span>}
                </label>
                <input
                  type="number"
                  inputMode="decimal"
                  className={`entry-input ${!canEditAdblue ? 'input-locked' : ''}`}
                  placeholder="pl. 20"
                  value={adblue}
                  onChange={e => setAdblue(e.target.value)}
                  disabled={!canEditAdblue}
                />
              </div>
            </div>
          )}

          {error && <p className="modal-error">{error}</p>}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Mégse</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Mentés...' : 'Módosítás mentése'}
          </button>
        </div>
      </div>
    </div>
  );
}